import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';

import MessagePropType from '../definitions/MessagePropType';
import toKebabCase from '../functions/toKebabCase';
import messageToString from '../functions/messageToString';
import replaceNonAlphanumeric from '../functions/replaceNonAlphanumeric';
import HorizontalTabs from './HorizontalTabs';


function ProductCardTabFooter({
    initialTabIndex = null,
    tabBar,
    tabPanelContent,
    productId,

    borderRadius = '8px',
    className,
}) {
    const intl = useIntl();
    // Starts off as null - these tabs are closed by default
    const [ activeTabIndex, setActiveTabIndex ] = useState(null);

    // set active tab if we have an initialTabIndex
    useEffect(() => {
        if (initialTabIndex !== null) {
            setActiveTabIndex(initialTabIndex);
        }
    }, [ initialTabIndex ]);

    const prodId = replaceNonAlphanumeric(productId);
    const tabIdPrefix = `tab_${prodId}`;
    const tabPanelIdPrefix = `panel_${prodId}`;

    function handleTabChange(selectedKey) {
        setActiveTabIndex(selectedKey);
    }

    return (
        <Container
            className={className}
            borderRadius={borderRadius}
        >
            <HorizontalTabs
                className={classNames('rh-bg-stone-lightest', {
                    'rounded-bottoms': activeTabIndex === null,
                    'flat-bottom': activeTabIndex !== null,  // when showing a tab
                })}
                variant="chevrons"
                enableAccordionToggle={true}

                onTabChange={handleTabChange}

                initialTabIndex={initialTabIndex}
                tabIdPrefix={tabIdPrefix}
                tabPanelIdPrefix={tabPanelIdPrefix}
                tabs={tabBar}
            />

            <For
                each="tab"
                of={tabPanelContent}
                index="index"
            >
                <div
                    role="tabpanel"
                    className={classNames('rounded-bottoms rh-bg-coconut', {
                        'rh-display-none': activeTabIndex !== index,
                    })}
                    aria-hidden={activeTabIndex !== index}
                    id={`${tabPanelIdPrefix}_${index}`}
                    aria-labelledby={`${tabIdPrefix}_${index}`}
                    key={index}
                    data-test-name={`${tabPanelIdPrefix}_${toKebabCase(messageToString(tabBar[index].title, intl))}`}
                >
                    {tabPanelContent[index]}
                </div>
            </For>
        </Container>
    );
}


ProductCardTabFooter.propTypes = {
    initialTabIndex: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.oneOf([ null ]),
    ]),

    // tabBar & tabPanelContent _must_ have the same length and be in the same order
    tabBar: PropTypes.arrayOf(
        PropTypes.shape({
            title: MessagePropType,
            // ProductCardTabFooter has no use for a subTitle
        }),
    ).isRequired,
    tabPanelContent: PropTypes.arrayOf(
        PropTypes.node.isRequired,
    ).isRequired,
    productId: PropTypes.string,

    borderRadius: PropTypes.string,
    className: PropTypes.string,
};


const Container = styled.div`
    // provide border-radius on bottom-left and -right when the HorizontalTab is the bottom of the card
    // or when the tab content we provide is the bottom
    .rounded-bottoms {
        ${({ borderRadius }) => `border-radius: 0 0 calc(${borderRadius} - 1px) calc(${borderRadius} - 1px)`};
    }

    > .flat-bottom {
        border-radius: unset;
    }
`;


export default observer(ProductCardTabFooter);
